import {Component, OnInit, AfterViewInit, ViewChild} from '@angular/core';
import {ParticipantRoleAndPermissionsViewModel} from "../models/participant-role-and-permissions-view-model";
import {Subject} from "rxjs";
import {HttpClient} from "@angular/common/http";
import {DataTablesResponseViewModel} from "../models/data-tables-response-view-model";
import {environment} from "../../environments/environment";
import CellMetaSettings = DataTables.CellMetaSettings;
import {RspCodeRspMapper} from "../models/mappers";
import {SettlementService} from "../services/settlement.service";
import {DataTableDirective} from "angular-datatables";
import {NgbCalendar, NgbDate, NgbDateParserFormatter, NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {DatePipe} from "@angular/common";
import {debounceTime, distinctUntilChanged} from "rxjs/operators";
import {HoldDialogComponent} from "./hold-dialog/hold-dialog.component";
import {ReleaseDialogComponent} from "./release-dialog/release-dialog.component";
import {PermissionEnum} from '../services/guards/permissions';
import {UserDataStoreService} from '../services/user-data-store.service';
import {ToastrService} from 'ngx-toastr';
import {Router} from '@angular/router';
import {InfoDialogComponent} from "./info-dialog/info-dialog.component";

@Component({
  selector: 'app-settlement',
  templateUrl: './settlement.component.html',
  styleUrls: ['./settlement.component.scss']
})
export class SettlementComponent implements OnInit, AfterViewInit {

  public displayMonths = 0;
  public navigation = 'select';
  public showWeekNumbers = false;
  public outsideDays = 'hidden';

  hoveredDate: NgbDate;
  fromDate: NgbDate;
  toDate: NgbDate;

  public canView: boolean = false;
  public canHold: boolean = false;
  public canRelease: boolean = false;

  public Action_Hold: string;
  public Action_Release: string;

  @ViewChild(DataTableDirective) public dtElement: DataTableDirective;

  public settlements: any[] = [];

  public get displayColumns() {
    return this.dtOptions.columns.filter(x => x.visible != false);
  }

  public searchableColumns: any = {
    DatetimeTranLocal: {searchable: true, inputType: 'dateRange'},
    MerchantId: {searchable: true, inputType: 'string'},
    TerminalId: {searchable: true, inputType: 'string'},
    TransactionStatus: {searchable: true, inputType: 'transactionStatus'},
    Amount: {searchable: true, inputType: 'currency'}
  };


  public terminalIdSearch = '';

  public merchantIdSearch = '';

  public get dateRangeSearch(): string {
    if (!this.fromDate || !this.toDate) {
      return '';
    }
    return `?fromDate=${new Date(this.fromDate.year, this.fromDate.month - 1, this.fromDate.day).toISOString()}&toDate=${new Date(this.toDate.year, this.toDate.month - 1, this.toDate.day).toISOString()}`;
  }

  public columns: any[] = [
    {data: 'PostTranId', visible: false},
    {
      data: 'DatetimeTranLocal', title: 'Date Time', visible: true, width: '100px',
      render: (data: any) => {
        return this.datePipe.transform(data, 'medium');
      },
      searchable: true, search: {value: undefined, regex: false, range: true}
    },
    {data: 'MerchantId', title: 'Merchant Id', visible: true, searchable: true, search: {value: this.merchantIdSearch, regex: true}},
    {data: 'TerminalId', title: 'Terminal Id', visible: true, searchable: true, search: {value: this.terminalIdSearch, regex: true}},
    {data: 'ParticipantId', title: 'Participant Id', visible: false},
    {data: 'TranType', title: 'Transaction Type', visible: true},
    {data: 'MaskedCard', title: 'Card', visible: true},
    {data: 'TransactionHold', visible: false},
    {data: 'MerchantHold', visible: false},
    {data: 'TerminalHold', visible: false},
    {
      data: 'Amount', title: 'Amount', visible: true, render: (data: any) => {
        return (data / 100).toLocaleString("en-ZA", {style: "currency", currency: "ZAR"});
      }
    },
    {data: 'HoldBy', title: 'HoldBy', visible: false},
    {data: 'HoldReason', title: 'HoldReason', visible: false},
    {data: 'HoldAt', title: 'HoldAt', visible: false},
    {data: 'ReleaseBy', title: 'ReleaseBy', visible: false},
    {data: 'ReleaseReason', title: 'ReleaseReason', visible: false},
    {data: 'ReleaseAt', title: 'ReleaseAt', visible: false},
  ];

  private searchColumns = this.columns.map((col) => {
    if (!!col.search) {
      return {search: col.search.value, regex: col.search.regex};
    } else {
      return null;
    }
  });
  // @ts-ignore
  public dtOptions: DataTables.Settings = {
    pageLength: 10,
    serverSide: true,
    processing: true,
    ajax: (dataTablesParameters: any, callback) => {
      this._httpClient
        .post<DataTablesResponseViewModel>(
          `${environment.apiUrl}/api/settlement/search${this.dateRangeSearch}`,
          dataTablesParameters, {}
      ).subscribe(resp => {
        this.settlements = resp.data;

        callback({
          recordsTotal: resp.recordsTotal,
          recordsFiltered: resp.recordsFiltered,
          data: []
        });
      });
    },
    columns: this.columns,
    searchCols: this.searchColumns

  };

  public dtTrigger: Subject<any> = new Subject();

  public searchDebounce = new Subject<string>();

  constructor(private _httpClient: HttpClient, private _settlementService: SettlementService, private calendar: NgbCalendar, public formatter: NgbDateParserFormatter, private datePipe: DatePipe, private modalService: NgbModal, private _userDataStore: UserDataStoreService, private _toastService: ToastrService, private _router: Router) {

    this.searchDebounce.pipe(
      debounceTime(800),
      distinctUntilChanged())
      .subscribe(async value => {
        await this.refreshTable();
      });


    this.canView = this._userDataStore.hasRequiredPermissions([PermissionEnum.ViewSettlement], []);
    if (!this.canView) {
      this._toastService.warning("You do not have the required permissions to view this page, Please contact your system administrator....", "Warning");
      this._router.navigate(['/']);
    }


    this.canHold = this._userDataStore.hasRequiredPermissions([PermissionEnum.HoldSettlement], []);
    if (!this.canHold) {
      this.Action_Hold = "display:none";
    }

    this.canRelease = this._userDataStore.hasRequiredPermissions([PermissionEnum.ReleaseSettlement], []);
    if (!this.canRelease) {
      this.Action_Release = "display:none";
    }

  }

  ngOnInit() {
  }

  ngAfterViewInit(): void {
    this.dtTrigger.next();
  }

  getDateFilterValue() {
    if (this.fromDate && this.toDate) {
      return this.datePipe.transform(this.formatter.format(this.fromDate)) + ' to ' + this.datePipe.transform(this.formatter.format(this.toDate));
    } else {
      return null;
    }
  }

  public setupFilters(): void {
    const searchColumns = this.columns.map((col) => {
      if (!!(col).search) {
        return {search: (col).search.value, regex: (col).search.regex};
      } else {
        return null;
      }
    });
    //this.dtOptions.searchCols = searchColumns;
    this.dtElement.dtInstance.then((dtInstance: DataTables.Api) => {
      let i = 0;
      for (const item of searchColumns) {
        if (item) {
          dtInstance.column(i).search(item.search, item.regex);
        }
        i++;
      }
    })
  }

  onDateSelection(date: NgbDate) {
    if (!this.fromDate && !this.toDate) {
      this.fromDate = date;
    } else if (this.fromDate && !this.toDate && date.after(this.fromDate)) {
      this.toDate = date;
    } else {
      this.toDate = null;
      this.fromDate = date;
    }
    this.searchDebounce.next();
  }

  isHovered(date: NgbDate) {
    return this.fromDate && !this.toDate && this.hoveredDate && date.after(this.fromDate) && date.before(this.hoveredDate);
  }

  isInside(date: NgbDate) {
    return date.after(this.fromDate) && date.before(this.toDate);
  }

  isRange(date: NgbDate) {
    return date.equals(this.fromDate) || date.equals(this.toDate) || this.isInside(date) || this.isHovered(date);
  }

  validateInput(currentValue: NgbDate, input: string): NgbDate {
    const parsed = this.formatter.parse(input);
    return parsed && this.calendar.isValid(NgbDate.from(parsed)) ? NgbDate.from(parsed) : currentValue;
  }

  private async refreshTable(): Promise<void> {
    await this.dtElement.dtInstance.then((dtInstance: DataTables.Api) => {
      // Destroy the table first
      dtInstance.destroy();
      this.setupFilters();
      // Call the dtTrigger to rerender again
      this.dtTrigger.next();
    });
  }

  private async filterChanged(): Promise<void> {
    await this.refreshTable();
  }


  public async holdTransactionClick(settlement: any): Promise<void> {
    const modalRef = this.modalService.open(HoldDialogComponent, {
      windowClass: 'modal-fade-in',
      centered: false,
      size: 'xl',
      backdrop: 'static'
    });
    const result: { holdType?: 'single' | 'merchant' | 'terminal', reason: string } = await modalRef.result;

    switch (result.holdType) {
      case "single":
        await this._settlementService.HoldTransaction({
          postTranId: this.getParameterCaseInsensitive(settlement, 'PostTranId'),
          reason: result.reason
        });
        this.setParameterCaseInsensitive(settlement, 'TransactionHold', true);
        break;
      case "merchant":
        await this._settlementService.HoldTransaction({
          merchantId: this.getParameterCaseInsensitive(settlement, 'MerchantId'),
          reason: result.reason
        });
        await this.refreshTable();
        break;
      case "terminal":
        await this._settlementService.HoldTransaction({
          terminalId: this.getParameterCaseInsensitive(settlement, 'TerminalId'),
          reason: result.reason
        });
        await this.refreshTable();
        break;
    }

  }

  public async releaseTransactionClick(settlement: any): Promise<void> {
    const modalRef = this.modalService.open(ReleaseDialogComponent, {
      windowClass: 'modal-fade-in',
      centered: false,
      size: 'xl',
      backdrop: 'static'
    });

    if (this.getParameterCaseInsensitive(settlement, 'TransactionHold')) {
      modalRef.componentInstance.holdType = 'single';
    }

    if (this.getParameterCaseInsensitive(settlement, 'TerminalHold')) {
      modalRef.componentInstance.holdType = 'terminal';
    }

    if (this.getParameterCaseInsensitive(settlement, 'MerchantHold')) {
      modalRef.componentInstance.holdType = 'merchant';
    }

    const result: { holdType?: 'single' | 'merchant' | 'terminal', reason: string } = await modalRef.result;

    if (!!result) {
      switch (result.holdType) {
        case "single":
          await this._settlementService.ReleaseTransaction({
            postTranId: this.getParameterCaseInsensitive(settlement, 'PostTranId'),
            reason: result.reason,
            releaseAt: new Date()
          });
          this.setParameterCaseInsensitive(settlement, 'TransactionHold', false);
          break;
        case "merchant":
          await this._settlementService.ReleaseTransaction({
            merchantId: this.getParameterCaseInsensitive(settlement, 'MerchantId'),
            reason: result.reason,
            releaseAt: new Date()
          });
          await this.refreshTable();
          break;
        case "terminal":
          await this._settlementService.ReleaseTransaction({
            terminalId: this.getParameterCaseInsensitive(settlement, 'TerminalId'),
            reason: result.reason,
            releaseAt: new Date()
          });
          await this.refreshTable();
          break;
      }
    }
  }

  public async displayInfo(settlement: any): Promise<void> {
    const modalRef = this.modalService.open(InfoDialogComponent, {
      windowClass: 'modal-fade-in',
      centered: false,
      size: 'xl',
      backdrop: 'static'
    });

    modalRef.componentInstance.HoldBy = this.resolveData(settlement, 'HoldBy');
    modalRef.componentInstance.HoldReason = this.resolveData(settlement, 'HoldReason');
    modalRef.componentInstance.HoldAt = this.resolveData(settlement, 'HoldAt');
    modalRef.componentInstance.ReleaseBy = this.resolveData(settlement, 'ReleaseBy');
    modalRef.componentInstance.ReleaseReason = this.resolveData(settlement, 'ReleaseReason');
    modalRef.componentInstance.ReleaseAt = this.resolveData(settlement, 'ReleaseAt');

    await modalRef.result;
  }

  public resolveData(item: any, col: string, render?: (data: any, type: any, row: any, meta: CellMetaSettings) => any) {
    if (render) {
      return render(this.getParameterCaseInsensitive(item, col), null, null, null);
    }
    return this.getParameterCaseInsensitive(item, col);
  }

  public getParameterCaseInsensitive(object, key) {
    if (object && key) {
      return object[Object.keys(object)
        .find(k => k.toLowerCase() === key.toLowerCase())
        ];
    }
    return null;
  }

  public setParameterCaseInsensitive(object, key, value) {
    if (object && key) {
      object[Object.keys(object)
        .find(k => k.toLowerCase() === key.toLowerCase())
        ] = value;
    }
  }


}
